import { v4 } from "uuid";
import { iChatData } from "../../types";
import { dropEmptyContentMessages } from "../../streaming/finalizeGuards";
import { Exchange, Fragment } from "../../services/voice/liveTranscript";
import { TranscriptCleaner } from "../../services/voice/transcriptCleaner";
import { withCheckoutNotes } from "../../utils/chat/checkoutNotes";
import type { Turn, VoiceSession } from "./types";

export const latestPrompt = (chatData: iChatData[]): any[] =>
  withCheckoutNotes(
    chatData,
    [...chatData].reverse().find((entry) => Array.isArray(entry?.prompt) && entry.prompt.length > 0)?.prompt ?? []
  );

export const exchangeAt = (chatData: iChatData[], userEntryId: string | null): { user: number; reply: number } | null => {
  const user = chatData.findIndex((entry) => entry.id === userEntryId);
  if (user < 0) return null;
  const reply = chatData.findIndex((entry, index) => index > user && !entry.isUserMessage);
  return reply < 0 ? null : { user, reply };
};

export const withVoiceReply = (chatData: iChatData[], turn: Turn, words: string): iChatData[] => {
  const at = exchangeAt(chatData, turn.userEntryId);
  if (!at) return chatData;
  const next = chatData.slice();
  next[at.reply] = {
    ...next[at.reply],
    voiceReply: words,
    ...(turn.liveSessionId ? { liveSessionId: turn.liveSessionId } : {}),
  };
  return next;
};

export const voiceUserEntry = (text: string, liveSessionId: string | null): iChatData => ({
  id: v4(),
  message: text,
  isUserMessage: true,
  prompt: [],
  isLoading: false,
  destinationList: [],
  singleSelectedDestination: false,
  inputMode: "voice",
  ...(liveSessionId ? { liveSessionId } : {}),
});

export const withFailedExchange = (chatData: iChatData[], userEntryId: string | null, request: string | null): iChatData[] => {
  const at = exchangeAt(chatData, userEntryId);
  if (!at) return chatData;
  const next = chatData.slice();
  next[at.reply] = {
    ...next[at.reply],
    prompt: [
      ...dropEmptyContentMessages(latestPrompt(chatData.slice(0, at.user))),
      { role: "user", content: request ?? chatData[at.user].maskedMessage ?? chatData[at.user].message },
      { role: "assistant", content: chatData[at.reply].message },
    ],
  };
  return next;
};

export const withCleanPrompts = (chatData: iChatData[], raw: string, clean: string): iChatData[] =>
  chatData.map((entry) =>
    Array.isArray(entry.prompt) && entry.prompt.some((message: any) => message?.content === raw)
      ? { ...entry, prompt: entry.prompt.map((message: any) => (message?.content === raw ? { ...message, content: clean } : message)) }
      : entry,
  );

export const withMessage = (chatData: iChatData[], entryId: string, message: string): iChatData[] =>
  chatData.map((entry) => (entry.id === entryId ? { ...entry, message } : entry));

export const gotBusyNote = (session: VoiceSession, exchange: Exchange): boolean =>
  exchange.user.some((fragment) => session.busy.has(fragment));

export const bubbleText = (cleaner: TranscriptCleaner, fragments: Fragment[]): string =>
  cleaner.cleaned(fragments).text || cleaner.best(fragments);
